import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from 'contexts/AuthContext'
import { Button } from 'antd'
import { ScreenKey } from './util'
import { logBrazeEvent } from 'utils/brazeInit'
import { useDeeplink } from 'hooks/useDeeplink'
import logoIcon from 'static/img/fiton-care-logo.png'
import welcomeImg from './img/care-welcome.png'
import welcomeWlcImg from './img/ic-welcome-wlc.png'
import welcomePtImg from './img/bg-welcome-pt.png'
import bgWelcome from './img/ic-rehab-welcome.png'
import bgWelcomeMobile from './img/ic-rehab-welcome-mobile.png'
import bgWelcomeMaleMobile from './img/ic-rehab-welcome-male-mobile.png'
import logoIconWhite from 'static/img/fiton-care-logo-white.svg'
import bgWelcomeMale from './img/ic-rehab-welcome-male.png'
import closeIcon from 'static/img/ic-close-header.svg'
import welcomeMcaImg from './img/bg-welcome-mca.png'
import welcomeMcaMobileImg from './img/bg-welcome-mca-mobile.png'
import { isMobile } from 'utils/basic'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getFitonToken, getLocalLanguage } from 'utils/authLocalStorage'
import { useDarkMode } from 'hooks/useDarkMode'
import { LoadingContent } from 'components/LoadingDialog/index'
import { getWebFitOnHost } from 'apis/formatUrl'
import { USE_NATIVE_MCA_SIGNUP } from 'config'
import { useMcaEnrollment } from 'hooks/useMcaEnrollment'

const Welcome = (props) => {
  const { setCurrentKey, sku, gender, showClose, onClose } = props

  const { i18n } = useI18n()
  const {
    theme,
    variant,
    utmCampaign,
    utmContent,
    utmSource,
    fitonUserId,
    auth,
  } = useDeeplink()
  const { isForcedDarkMode } = useDarkMode()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { tryAutoLoginFromStoredSession } = useMcaEnrollment()

  useEffect(() => {
    props.setRemoveEventListener(true)
  }, [])

  // Previously bounced out to fiton-web's own /signup page
  // (docs/fiton-care-web-native-signup-login-plan.md, Step 3). Now that
  // /signup and /login are native fiton-care-web routes, this is an in-app
  // navigation instead of a full-page redirect.
  //
  // variant/gender are threaded through Signup -> AboutYou -> the final
  // auto-login handoff back into the Care flow (useFitonAuth.js's
  // buildFitOnCareHandoffQuery) — without `variant`, useFlow.js has no way to
  // know this should resume as the MCA flow instead of the generic one.
  //
  // USE_NATIVE_MCA_SIGNUP (config.js) is a manual backdoor, not a real
  // feature flag: flip it to `false` (and redeploy) to fall straight back to
  // the original fiton-web redirect below, untouched, if the native flow
  // needs to be pulled.
  //
  // Opens the Login view instead of Sign Up when the link asks for it
  // (auth=login) or carries `u=` — a known FitOn user id, so the visitor
  // already has an account (CARE-158). Any other `auth` value falls back to
  // Sign Up. Same router state either way, so /login keeps the MCA analytics
  // and post-login handoff that /signup gets.
  const redirectToMedicareSignup = useCallback(() => {
    if (!USE_NATIVE_MCA_SIGNUP) {
      window.location.href = getWebFitOnHost() + '/signup?r=medicare-signup'
      return
    }
    const authPath = auth === 'login' || fitonUserId ? '/login' : '/signup'
    navigate(authPath, { state: { variant, gender } })
  }, [navigate, variant, gender, auth, fitonUserId])

  // MCA links carrying `u=` skip this screen and go straight to Login, so the
  // visitor never actually sees Welcome.
  const shouldAutoRedirect = Boolean(fitonUserId) && variant === 'mca'

  useEffect(() => {
    if (shouldAutoRedirect) {
      setIsLoading(true)
      redirectToMedicareSignup()
    }
  }, [shouldAutoRedirect, redirectToMedicareSignup])

  const currentLogoIcon = useMemo(() => {
    return isForcedDarkMode ? logoIconWhite : logoIcon
  }, [isForcedDarkMode])

  const getWelcomeInfo = (sku, gender) => {
    const mobile = isMobile()
    switch (sku) {
      case 'wlc':
        return {
          title: 'Join Our Weight Loss Companion Program',
          subtitle:
            'Your weight loss journey made simple with a program designed to guide, support, and empower you from day one to lasting success',
          image: welcomeWlcImg,
          buttonText: 'Get Started',
        }
      case 'pt':
        return {
          title: 'Welcome to FitOn Care!',
          subtitle:
            'We’re here to help you relieve pain, restore mobility, and rebuild strength on your recovery journey — guided by an experienced Physical Therapist every step of the way.',
          image: welcomePtImg,
          buttonText: 'Get Started',
        }
      case 'msk':
        const image = mobile
          ? gender === 'male'
            ? bgWelcomeMaleMobile
            : bgWelcomeMobile
          : gender === 'male'
            ? bgWelcomeMale
            : bgWelcome
        return {
          title: 'We’re here to support your recovery.',
          subtitle:
            'We have a few questions for you to better understand your discomfort or pain.',
          image,
          buttonText: 'Get Started',
        }
      case 'mca':
        return {
          title: 'Join FitOn Care',
          subtitle:
            'Our Medicare ACCESS wellness programs are designed to help you lose weight and reduce joint & muscle pain - at no cost to you. <br /><br /> Build healthier habits and feel your best with no changes to your Medicare benefits.',
          image: mobile ? welcomeMcaMobileImg : welcomeMcaImg,
          buttonText: 'Get Started',
        }
      default:
        return {
          title: 'Welcome to FitOn Care!',
          subtitle:
            "We're here to help you reach your health & wellness goals by pairing you with an experienced Registered Dietitian for personalized nutrition counseling.",
          image: welcomeImg,
          buttonText: 'Get Started',
        }
    }
  }

  useEffect(() => {
    switch (sku) {
      case 'wlc':
      case 'pt':
      case 'msk':
      case 'mca':
        logBrazeEvent(`Onboarding Start - ${sku}`)
        break
      default:
        break
    }
    // The screen is never shown when auto-redirecting, so don't log a view of
    // it (CARE-164). The Braze onboarding start above still fires.
    if (shouldAutoRedirect) return
    logAmplitudeEvent('Screen View: Onboarding - Welcome', {
      ...(variant === 'prescreen' ? { Type: 'Prescreen Questions' } : {}),
      ...(sku === 'mca'
        ? { theme: 'medicare' }
        : theme
          ? { theme: theme }
          : {}),
      sku: sku,
      language: getLocalLanguage(),
      utmCampaign,
      utmContent,
      utmSource,
    })
  }, [
    sku,
    variant,
    theme,
    utmCampaign,
    utmContent,
    utmSource,
    shouldAutoRedirect,
  ])

  const welcomeInfo = useMemo(() => {
    return getWelcomeInfo(sku, gender)
  }, [sku, gender])

  const handleGetStarted = useCallback(async () => {
    switch (sku) {
      case 'msk':
        if (theme) {
          setCurrentKey(ScreenKey.REHAB_QUESTION_1)
        } else {
          setCurrentKey(ScreenKey.PAIN_LOCATED)
        }
        break
      case 'mca':
        // A FitOn token already on this device means the visitor has signed
        // up/logged in natively before — resume that session instead of
        // sending them through the signup/login form again.
        if (getFitonToken()) {
          setIsLoading(true)
          const didAutoLogin = await tryAutoLoginFromStoredSession({
            variant,
            gender,
          })
          setIsLoading(false)
          if (didAutoLogin) return
        }
        redirectToMedicareSignup()
        break
      default:
        setCurrentKey(ScreenKey.PERSONAL_INFO)
    }
  }, [
    sku,
    theme,
    setCurrentKey,
    redirectToMedicareSignup,
    variant,
    gender,
    tryAutoLoginFromStoredSession,
  ])

  if (isLoading) {
    return (
      <div className='loading'>
        <LoadingContent />
      </div>
    )
  }
  return (
    <div className='welcome-sec'>
      <img src={currentLogoIcon} alt='' className='logo-icon' />
      <img src={welcomeInfo.image} alt='' className='welcome-img' />
      {showClose && (
        <img
          src={closeIcon}
          alt=''
          className='close-icon'
          style={{ top: '13px', transform: 'none' }}
          onClick={onClose}
        />
      )}
      <div
        className='title'
        style={{ maxWidth: '478px', margin: '40px auto 0' }}
      >
        {i18n(welcomeInfo.title)}
      </div>
      <div
        className='subtitle'
        style={{
          marginTop: '16px',
          marginBottom: '48px',
          maxWidth: '510px',
          margin: '16px auto',
          padding: '0 16px',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: i18n(welcomeInfo.subtitle) }} />
      </div>
      <Button className='btn-primary large-btn' onClick={handleGetStarted}>
        {i18n(welcomeInfo.buttonText)}
      </Button>
    </div>
  )
}

export default Welcome
